import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

import { api, formatApiError } from "../../../services/api";
import { Button } from "../../../components/ui/button";

/**
 * Public order confirmation page. Reached from the email link
 * `${FRONTEND_URL}/confirm/{token}`. No auth guard.
 */
export default function ConfirmOrderPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action") === "reject" ? "reject" : null;
  const { t, i18n } = useTranslation();

  const [state, setState] = useState({
    loading: true,
    error: null,
    order: null,
    canConfirm: false,
  });
  // 'idle' | 'confirming' | 'rejecting' | 'confirmed' | 'rejected' | 'error'
  const [phase, setPhase] = useState(initialAction === "reject" ? "rejecting" : "idle");
  const [phaseError, setPhaseError] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get(`/public/orders/preview/${token}`);
      setState({
        loading: false,
        error: null,
        order: data.order,
        canConfirm: !!data.can_confirm,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code || "INVALID";
      setState({
        loading: false,
        error: { code, message: formatApiError(err, t("confirm_page.error_generic")) },
        order: null,
        canConfirm: false,
      });
    }
  }, [token, t]);

  useEffect(() => {
    load();
  }, [load]);

  const doAction = async (action) => {
    setPhase(action === "confirm" ? "confirming" : "rejecting");
    setPhaseError(null);
    try {
      const path = action === "confirm" ? "/public/orders/confirm" : "/public/orders/reject";
      await api.post(path, { token });
      setPhase(action === "confirm" ? "confirmed" : "rejected");
    } catch (err) {
      setPhaseError(formatApiError(err, t("confirm_page.error_generic")));
      setPhase("error");
    }
  };

  const langToggle = () => {
    const next = i18n.language === "de" ? "en" : "de";
    i18n.changeLanguage(next);
  };

  return (
    <div className="min-h-screen bg-offwhite text-text-primary">
      <header className="border-b border-border-subtle bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <div className="flex items-baseline gap-3">
            <span className="text-lg font-semibold tracking-tight" data-testid="confirm-page-brand">
              Moët Hennessy Germany
            </span>
            <span className="text-xs text-text-secondary">
              {t("confirm_page.subtitle")}
            </span>
          </div>
          <button
            type="button"
            onClick={langToggle}
            data-testid="confirm-lang-toggle"
            className="rounded-full border border-border-subtle px-3 py-1 text-xs font-medium uppercase tracking-wide text-text-secondary transition-colors hover:bg-offwhite"
          >
            {i18n.language === "de" ? "EN" : "DE"}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        {state.loading ? (
          <div
            className="flex flex-col items-center gap-3 rounded-lg bg-surface p-10 shadow-card"
            data-testid="confirm-loading"
          >
            <Loader2 className="h-6 w-6 animate-spin text-taupe" aria-hidden />
            <p className="text-sm text-text-secondary">{t("common.loading")}</p>
          </div>
        ) : state.error ? (
          <ErrorPanel code={state.error.code} message={state.error.message} />
        ) : phase === "confirmed" ? (
          <ResultPanel
            variant="success"
            title={t("confirm_page.confirmed_title")}
            body={t("confirm_page.confirmed_body")}
            order={state.order}
            testId="confirm-result-confirmed"
          />
        ) : phase === "rejected" ? (
          <ResultPanel
            variant="danger"
            title={t("confirm_page.rejected_title")}
            body={t("confirm_page.rejected_body")}
            order={state.order}
            testId="confirm-result-rejected"
          />
        ) : (
          <OrderPreview
            order={state.order}
            canAct={state.canConfirm}
            phase={phase}
            phaseError={phaseError}
            onConfirm={() => doAction("confirm")}
            onReject={() => doAction("reject")}
          />
        )}
      </main>
    </div>
  );
}

function OrderPreview({ order, canAct, phase, phaseError, onConfirm, onReject }) {
  const { t } = useTranslation();
  const isBusy = phase === "confirming" || phase === "rejecting";
  return (
    <section className="rounded-lg bg-surface p-8 shadow-card" data-testid="confirm-preview">
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="confirm-title">
        {t("confirm_page.title", {
          order: order?.order_number ? `#${order.order_number}` : "",
        })}
      </h1>
      <p className="mt-2 text-sm text-text-secondary">
        {t("confirm_page.intro")}
      </p>

      <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
        <Field label={t("confirm_page.field_event")} value={order?.event_name || "—"} />
        <Field label={t("confirm_page.field_customer")} value={order?.customer_company || "—"} />
        <Field
          label={t("confirm_page.field_from")}
          value={order?.from_date || "—"}
          mono
        />
        <Field
          label={t("confirm_page.field_until")}
          value={order?.until_date || "—"}
          mono
        />
        <Field
          label={t("confirm_page.field_bottles")}
          value={String(order?.bottles_count ?? 0)}
          mono
        />
      </dl>

      <div className="mt-6">
        <h2 className="text-sm font-semibold text-text-primary">
          {t("confirm_page.items_heading")}
        </h2>
        <ul className="mt-2 divide-y divide-border-subtle rounded-md border border-border-subtle bg-offwhite/50">
          {(order?.items || []).map((it, i) => (
            <li
              key={i}
              className="flex items-center justify-between px-3 py-2 text-sm"
              data-testid={`confirm-item-${i}`}
            >
              <span className="text-text-primary">{it.product_name}</span>
              <span className="font-mono text-xs text-text-secondary">
                × {it.quantity}
              </span>
            </li>
          ))}
          {(!order?.items || order.items.length === 0) && (
            <li className="px-3 py-2 text-sm text-text-secondary">—</li>
          )}
        </ul>
      </div>

      {phaseError && (
        <p className="mt-4 text-sm text-status-unavailable" data-testid="confirm-phase-error">
          {phaseError}
        </p>
      )}

      <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onReject}
          disabled={!canAct || isBusy}
          data-testid="confirm-reject-button"
          className="border-status-unavailable/40 text-status-unavailable hover:bg-status-unavailable/10"
        >
          {phase === "rejecting" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <XCircle className="mr-2 h-4 w-4" />
          )}
          {t("confirm_page.reject_button")}
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          disabled={!canAct || isBusy}
          data-testid="confirm-confirm-button"
          className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
        >
          {phase === "confirming" ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <CheckCircle2 className="mr-2 h-4 w-4" />
          )}
          {t("confirm_page.confirm_button")}
        </Button>
      </div>

      {!canAct && (
        <p className="mt-4 text-xs text-text-secondary" data-testid="confirm-not-pending">
          {t("confirm_page.not_pending_note")}
        </p>
      )}
    </section>
  );
}

function ResultPanel({ variant, title, body, order, testId }) {
  const isSuccess = variant === "success";
  const Icon = isSuccess ? CheckCircle2 : XCircle;
  const tone = isSuccess
    ? "text-status-available"
    : "text-status-unavailable";
  return (
    <section
      className="rounded-lg bg-surface p-10 text-center shadow-card"
      data-testid={testId}
    >
      <Icon className={`mx-auto h-10 w-10 ${tone}`} aria-hidden />
      <h1 className="mt-4 text-xl font-semibold tracking-tight">{title}</h1>
      <p className="mt-2 text-sm text-text-secondary">{body}</p>
      {order?.order_number && (
        <p className="mt-4 font-mono text-xs text-text-secondary">
          #{order.order_number}
        </p>
      )}
    </section>
  );
}

function ErrorPanel({ code, message }) {
  const { t } = useTranslation();
  const titleByCode = {
    EXPIRED: t("confirm_page.error_expired_title"),
    LINK_ALREADY_USED: t("confirm_page.error_used_title"),
  };
  return (
    <section
      className="rounded-lg bg-surface p-10 text-center shadow-card"
      data-testid="confirm-error"
    >
      <XCircle className="mx-auto h-10 w-10 text-status-unavailable" aria-hidden />
      <h1 className="mt-4 text-xl font-semibold tracking-tight">
        {titleByCode[code] || t("confirm_page.error_generic_title")}
      </h1>
      <p className="mt-2 text-sm text-text-secondary">{message}</p>
    </section>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-text-secondary">
        {label}
      </dt>
      <dd
        className={`mt-1 text-sm text-text-primary ${
          mono ? "font-mono" : ""
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
